/**
 * Simulator form serializer
 *
 * Reads the values from a `data-controller="simulator-form"` root and produces
 * the camelCase JSON body expected by `POST /simulator/calculate`
 * (EPIC-026.3 §Endpoints API).
 *
 * Inputs that the maquette renders with `<input type="text">` (CA mensuel, RFR,
 * RC Pro…) are parsed leniently: thin/regular spaces stripped, comma → dot,
 * `parseFloat`. Empty strings become `null` for optional fields and `0` where
 * the API expects a number.
 */

function parseNumber(raw, { defaultValue = 0, allowNull = false } = {}) {
  if (raw === null || raw === undefined) {
    return allowNull ? null : defaultValue;
  }
  const cleaned = String(raw).replace(/[\s  ]/g, "").replace(",", ".").trim();
  if (cleaned === "") {
    return allowNull ? null : defaultValue;
  }
  const n = Number.parseFloat(cleaned);
  if (Number.isNaN(n)) {
    return allowNull ? null : defaultValue;
  }
  return n;
}

function parseInteger(raw, { defaultValue = 0, allowNull = false } = {}) {
  const n = parseNumber(raw, { defaultValue: NaN, allowNull: true });
  if (n === null || Number.isNaN(n)) {
    return allowNull ? null : defaultValue;
  }
  return Math.trunc(n);
}

function fieldValue(root, name) {
  const el = root.querySelector(`[data-field="${name}"]`);
  return el ? el.value : "";
}

function pressedValue(root, name) {
  const el = root.querySelector(`[data-toggle="${name}"][aria-pressed="true"]`);
  return el ? el.dataset.value : null;
}

function serializeActivity(card) {
  return {
    regime: card.querySelector('[data-activity-field="regime"]')?.value ?? "bic_services",
    category: card.querySelector('[data-activity-field="category"]')?.value ?? "none",
    monthlyCa: parseNumber(card.querySelector('[data-activity-field="monthlyCa"]')?.value, {
      defaultValue: 0,
    }),
    startDate:
      card.querySelector('[data-activity-field="startDate"]')?.value ||
      new Date().toISOString().slice(0, 10),
  };
}

export function serializeSimulatorForm(root) {
  const activities = Array.from(
    root.querySelectorAll('[data-simulator-form-target="activity"]'),
  ).map(serializeActivity);

  const vlOption = pressedValue(root, "vlOption") === "true";
  const salariedParallel = pressedValue(root, "salariedParallel") === "true";
  const allocation = fieldValue(root, "allocation") || "none";

  const tmiMap = { "0": 0, "11": 11, "30": 30, "41": 41, "45": 45 };
  const tmiRaw = fieldValue(root, "tmi");
  const tmi = vlOption ? null : (tmiMap[String(tmiRaw)] ?? 11);

  const acreEligible = pressedValue(root, "acreEligible") === "true";

  return {
    activities,
    vlOption,
    tmi,
    partsFiscales: vlOption ? parseInteger(fieldValue(root, "partsFiscales"), { defaultValue: 1 }) : null,
    rfrN2: vlOption ? parseNumber(fieldValue(root, "rfrN2"), { defaultValue: 0 }) : null,
    salariedParallel,
    allocation,
    areDailyNet:
      allocation === "are"
        ? parseNumber(fieldValue(root, "areDailyNet"), { defaultValue: 0 })
        : null,
    areRemainingDays:
      allocation === "are"
        ? parseInteger(fieldValue(root, "areRemainingDays"), { defaultValue: 0 })
        : null,
    arceTotal:
      allocation === "arce"
        ? parseNumber(fieldValue(root, "arceTotal"), { defaultValue: 0 })
        : null,
    arceFirstTrancheDate:
      allocation === "arce" ? fieldValue(root, "arceFirstTrancheDate") || null : null,
    acreEligible,
    rcProAnnual: parseNumber(fieldValue(root, "rcProAnnual"), { defaultValue: 0, allowNull: true }),
    mutuelleMonthly: parseNumber(fieldValue(root, "mutuelleMonthly"), { defaultValue: 0, allowNull: true }),
    prevoyanceMonthly: parseNumber(fieldValue(root, "prevoyanceMonthly"), { defaultValue: 0, allowNull: true }),
    cfeAmount: parseNumber(fieldValue(root, "cfeAmount"), { defaultValue: 0, allowNull: true }),
    simulationMonths: parseInteger(fieldValue(root, "simulationMonths"), { defaultValue: 12 }),
    simulationDate: fieldValue(root, "simulationDate") || null,
    targetNetMonthly: parseNumber(fieldValue(root, "targetNetMonthly"), {
      defaultValue: 0,
      allowNull: true,
    }),
  };
}

/**
 * Restores a previously-serialized state into the DOM. Only the fields that
 * exist in `root` are touched; missing fields are ignored.
 */
export function hydrateSimulatorForm(root, state) {
  if (!state || typeof state !== "object") return;

  const setField = (name, value) => {
    const el = root.querySelector(`[data-field="${name}"]`);
    if (el && value !== undefined && value !== null) el.value = value;
  };

  const setToggle = (name, value) => {
    const buttons = root.querySelectorAll(`[data-toggle="${name}"]`);
    buttons.forEach((btn) => {
      const matches = String(btn.dataset.value) === String(value);
      btn.setAttribute("aria-pressed", matches ? "true" : "false");
      btn.classList.toggle("nm-btn--solid", matches);
      btn.classList.toggle("nm-btn--ghost", !matches);
    });
  };

  if (Array.isArray(state.activities) && state.activities.length > 0) {
    hydrateActivities(root, state.activities);
  }

  setToggle("vlOption", state.vlOption ? "true" : "false");
  setToggle("salariedParallel", state.salariedParallel ? "true" : "false");
  setToggle("acreEligible", state.acreEligible ? "true" : "false");

  setField("tmi", state.tmi);
  setField("partsFiscales", state.partsFiscales);
  setField("rfrN2", state.rfrN2);
  setField("allocation", state.allocation);
  setField("areDailyNet", state.areDailyNet);
  setField("areRemainingDays", state.areRemainingDays);
  setField("arceTotal", state.arceTotal);
  setField("arceFirstTrancheDate", state.arceFirstTrancheDate);
  setField("rcProAnnual", state.rcProAnnual);
  setField("mutuelleMonthly", state.mutuelleMonthly);
  setField("prevoyanceMonthly", state.prevoyanceMonthly);
  setField("cfeAmount", state.cfeAmount);
  setField("simulationMonths", state.simulationMonths);
  setField("simulationDate", state.simulationDate);
  setField("targetNetMonthly", state.targetNetMonthly);
}

function hydrateActivities(root, activities) {
  const list = root.querySelector('[data-simulator-form-target="activitiesList"]');
  if (!list) return;

  const secondaryItems = list.querySelectorAll('li:has([data-activity-role="secondary"])');
  secondaryItems.forEach((li) => li.remove());

  const tpl = document.getElementById("tpl-activity-secondary");
  activities.forEach((activity, idx) => {
    let card;
    if (idx === 0) {
      card = list.querySelector('[data-activity-role="principal"]');
    } else if (tpl) {
      const fragment = tpl.content.cloneNode(true);
      const li = document.createElement("li");
      li.appendChild(fragment);
      list.appendChild(li);
      card = li.querySelector('[data-activity-role="secondary"]');
    }
    if (!card) return;
    setActivityField(card, "regime", activity.regime);
    setActivityField(card, "category", activity.category);
    setActivityField(card, "monthlyCa", activity.monthlyCa);
    setActivityField(card, "startDate", activity.startDate);
    if (activity.label !== undefined) setActivityField(card, "label", activity.label);
  });
}

function setActivityField(card, name, value) {
  const el = card.querySelector(`[data-activity-field="${name}"]`);
  if (el && value !== undefined && value !== null) el.value = value;
}
